import { PRODUCT_CODE, type AppEnvironment } from "../lib/env";
import type { CitywideUserProfile, PolicyNote, ProductEntitlement } from "../lib/types";
import { getCitywideCommonClient, getSeisakuEyeClient } from "../auth/supabaseClient";
import type { CreatePolicyNoteInput, PolicyRepository } from "./policyRepository";
import { RepositoryError } from "./policyRepository";

function requireClient<T>(client: T | null, label: string): NonNullable<T> {
  if (!client) throw new RepositoryError(`${label} is not configured.`);
  return client as NonNullable<T>;
}

function raise(message: string, error: unknown): never {
  throw new RepositoryError(message, error);
}

export class SupabasePolicyRepository implements PolicyRepository {
  async getUserProfile(userId: string) {
    const common = requireClient(getCitywideCommonClient(), "citywide_common");
    const { data, error } = await common.from("user_profiles").select("*").eq("user_id", userId).maybeSingle();
    if (error) raise("CityWide profile could not be loaded.", error);
    return data as CitywideUserProfile | null;
  }

  async upsertUserProfile(input: Pick<CitywideUserProfile, "user_id"> & Partial<CitywideUserProfile>) {
    const common = requireClient(getCitywideCommonClient(), "citywide_common");
    const { data, error } = await common
      .from("user_profiles")
      .upsert(
        {
          user_id: input.user_id,
          display_name: input.display_name ?? null,
          privacy_accepted_at: input.privacy_accepted_at ?? null,
        },
        { onConflict: "user_id" },
      )
      .select("*")
      .single();

    if (error) raise("CityWide profile could not be saved.", error);
    return data as CitywideUserProfile;
  }

  async getEntitlement(userId: string, environment: AppEnvironment) {
    const common = requireClient(getCitywideCommonClient(), "citywide_common");
    const { data, error } = await common
      .from("product_entitlements")
      .select("*")
      .eq("user_id", userId)
      .eq("product_code", PRODUCT_CODE)
      .eq("environment", environment)
      .maybeSingle();

    if (error) raise("Product entitlement could not be loaded.", error);
    return data as ProductEntitlement | null;
  }

  async listPolicyNotes(userId: string) {
    const seisaku = requireClient(getSeisakuEyeClient(), "seisaku_eye");
    const { data, error } = await seisaku
      .from("policy_notes")
      .select("*")
      .eq("user_id", userId)
      .order("updated_at", { ascending: false })
      .limit(12);

    if (error) raise("Policy notes could not be loaded.", error);
    return (data ?? []) as PolicyNote[];
  }

  async createPolicyNote(userId: string, input: CreatePolicyNoteInput) {
    const seisaku = requireClient(getSeisakuEyeClient(), "seisaku_eye");
    const { data, error } = await seisaku
      .from("policy_notes")
      .insert({
        user_id: userId,
        title: input.title,
        body: input.body,
        category: input.category ?? "policy_note",
        source_type: input.sourceType ?? "assistant",
        is_private: true,
      })
      .select("*")
      .single();

    if (error) raise("Policy note could not be saved.", error);
    return data as PolicyNote;
  }
}
